/**
 * MemorySettingsRoute — /settings/hukommelse, «Hva Stell husker» (AI-21, #193;
 * memory-plan 1f). Raden står på Meg for alle roller.
 *
 * Ikke i designhandoffen («Not designed yet» i design/PLAN.md): glasskortene,
 * etikettene og radene fra Admin, og bare eksisterende primitiver og
 * rolletokens (Pattern 8).
 *
 *   admin       «Venter på godkjenning» med Godta / Avvis øverst, så alle
 *               notater gruppert på «Hele familien» og hvert medlem, med
 *               Rediger, Slett og bryteren «Bare foreldre». Et notat dagens
 *               vakt ville avvist, får en advarsel (`guard_reason`, #232).
 *               Nederst en sammenfoldet «Arkivert»-gruppe med notatene om
 *               arkiverte medlemmer, som bare kan slettes (#227).
 *   tenåring /  «Venter på godkjenning» med egne ventende notater og Trekk
 *   barn        tilbake (#211), så notatene de kan se, Slett på dem de selv
 *               skrev, og linjen om at foreldrene kan se notatene deres
 *               (beslutning 2 i #190).
 *   tom         «Si «husk at …» i chatten».
 *
 * Rollen kommer fra `useSession`, som feiler lukket (#24): uten et bekreftet
 * admin-medlem vises barnevisningen. Serveren avgjør uansett hva som vises og
 * hva som kan endres (Pattern 5) — GET gir en tenåring bare det den kan se,
 * og en knapp klienten viser ved en feil, svarer 403/404.
 */
import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ArchivedMemberMemories, MemoryDTO } from '@stell/shared';
import { MEMORY_TEXT_MAX } from '@stell/shared';
import { ScreenHeader } from '../../components/ScreenHeader';
import { Button, EmptyState, ErrorState, Icon, LoadingState, Switch } from '../../components/primitives';
import { useSession } from '../auth/useSession';
import { useFamilyMembers } from '../chores/useFamilyMembers';
import { ADMIN_CARD, ADMIN_LABEL } from '../admin/adminStyles';
import { CONSOLE_MAIN } from '../parents/ParentConsoleRoute';
import {
  memoryErrorText,
  useDeleteMemory,
  useMemories,
  useResolveMemory,
  useUpdateMemory,
  useWithdrawMemory,
  withdrawErrorText,
} from '../assistant/useMemories';

type NameOf = (memberId: string | null) => string;

interface Group {
  key: string;
  label: string;
  notes: MemoryDTO[];
}

/** «Hele familien» først, så medlemmene i familiens rekkefølge, så de som ikke finnes lenger. */
function groupNotes(
  notes: readonly MemoryDTO[],
  members: readonly { id: string; name: string }[],
  wholeFamily: string,
  former: string,
): Group[] {
  const groups: Group[] = [{ key: 'family', label: wholeFamily, notes: notes.filter((n) => n.member_id === null) }];
  const known = new Set(members.map((m) => m.id));
  for (const m of members) {
    groups.push({ key: m.id, label: m.name, notes: notes.filter((n) => n.member_id === m.id) });
  }
  groups.push({
    key: 'former',
    label: former,
    notes: notes.filter((n) => n.member_id !== null && !known.has(n.member_id)),
  });
  return groups.filter((g) => g.notes.length > 0);
}

function RowError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <p role="alert" className="m-0 text-xs text-bad">
      {memoryErrorText(t, error)}
    </p>
  );
}

/**
 * Advarselen på et lagret notat dagens vakt ville avvist (#232). Serveren
 * sender `guard_reason` bare til admin og holder notatet utenfor prompten til
 * det er endret; årsaken er samme tekst som når vakten avviser en skriving.
 * Rediger og Slett står på raden fra før.
 */
function GuardFlag({ note, pending = false }: { note: MemoryDTO; pending?: boolean }) {
  const { t } = useTranslation();
  if (!note.guard_reason) return null;
  return (
    <p data-testid="memory-flagged" className="m-0 text-xs font-bold text-bad">
      {t(pending ? 'assistant.memory.settings.flagged_pending' : 'assistant.memory.settings.flagged')}{' '}
      {t(`assistant.memory.errors.${note.guard_reason}`, { defaultValue: t('assistant.memory.errors.generic') })}
    </p>
  );
}

function EditForm({ note, onDone }: { note: MemoryDTO; onDone: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateMemory();
  const [text, setText] = useState(note.text);
  const fieldId = useId();

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (text === note.text) {
      onDone();
      return;
    }
    update.mutate({ id: note.id, patch: { text } }, { onSuccess: onDone });
  }

  return (
    <form className="flex flex-col gap-2" onSubmit={submit}>
      <label htmlFor={fieldId} className={ADMIN_LABEL}>
        {t('assistant.memory.settings.edit_field')}
      </label>
      <textarea
        id={fieldId}
        data-testid="memory-edit-field"
        className="field"
        rows={2}
        maxLength={MEMORY_TEXT_MAX}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <RowError error={update.error} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size={44} data-testid="memory-edit-save" disabled={update.isPending}>
          {t('assistant.memory.settings.save')}
        </Button>
        <Button variant="ghost" size={44} onClick={onDone}>
          {t('assistant.memory.settings.cancel')}
        </Button>
      </div>
    </form>
  );
}

function NoteRow({
  note,
  isAdmin,
  canDelete,
}: {
  note: MemoryDTO;
  isAdmin: boolean;
  canDelete: boolean;
}) {
  const { t } = useTranslation();
  const del = useDeleteMemory();
  const update = useUpdateMemory();
  const [editing, setEditing] = useState(false);

  return (
    <li data-testid="memory-note" className="flex flex-col gap-1 border-t border-card-line py-2.5 first:border-t-0">
      {editing ? (
        <EditForm note={note} onDone={() => setEditing(false)} />
      ) : (
        <p className="m-0 text-sm font-bold text-ink">{note.text}</p>
      )}
      {isAdmin && !editing ? <GuardFlag note={note} /> : null}
      {!editing && (isAdmin || canDelete) ? (
        <div className="flex flex-wrap items-center gap-x-2">
          {isAdmin ? (
            <>
              {/* Synlig etikett; bryterens navn har notatteksten i tillegg,
                  så hver bryter i lista har sitt eget navn. */}
              <span aria-hidden className="text-caption font-bold text-ink-2">
                {t('assistant.memory.settings.parents_only')}
              </span>
              <Switch
                checked={note.audience === 'parents'}
                label={t('assistant.memory.settings.parents_only_label', { text: note.text })}
                data-testid="memory-audience"
                disabled={update.isPending}
                onChange={(next) =>
                  update.mutate({ id: note.id, patch: { audience: next ? 'parents' : 'all' } })
                }
              />
              <Button
                variant="secondary"
                size={44}
                aria-label={t('assistant.memory.settings.edit_label', { text: note.text })}
                onClick={() => setEditing(true)}
              >
                {t('assistant.memory.settings.edit')}
              </Button>
            </>
          ) : null}
          <Button
            variant="ghost"
            size={44}
            data-testid="memory-delete"
            aria-label={t('assistant.memory.settings.delete_label', { text: note.text })}
            disabled={del.isPending}
            onClick={() => del.mutate(note.id)}
          >
            {t('assistant.memory.settings.delete')}
          </Button>
        </div>
      ) : null}
      <RowError error={del.error ?? update.error} />
    </li>
  );
}

function PendingRow({ note, nameOf }: { note: MemoryDTO; nameOf: NameOf }) {
  const { t } = useTranslation();
  const resolve = useResolveMemory();
  return (
    <li data-testid="memory-pending" className="flex flex-col gap-1 border-t border-card-line py-2.5 first:border-t-0">
      <p className="m-0 text-sm font-bold text-ink">{note.text}</p>
      <GuardFlag note={note} pending />
      <p className="m-0 text-caption text-ink-2">
        {t('assistant.memory.settings.about', { name: nameOf(note.member_id) })}
        {note.created_by
          ? ` · ${t('assistant.memory.settings.from', { name: nameOf(note.created_by) })}`
          : null}
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size={44}
          data-testid="memory-approve"
          aria-label={t('assistant.memory.settings.approve_label', { text: note.text })}
          disabled={resolve.isPending}
          onClick={() => resolve.mutate({ id: note.id, decision: 'approve' })}
        >
          {t('assistant.memory.settings.approve')}
        </Button>
        <Button
          variant="secondary"
          size={44}
          data-testid="memory-reject"
          aria-label={t('assistant.memory.settings.reject_label', { text: note.text })}
          disabled={resolve.isPending}
          onClick={() => resolve.mutate({ id: note.id, decision: 'reject' })}
        >
          {t('assistant.memory.settings.reject')}
        </Button>
      </div>
      <RowError error={resolve.error} />
    </li>
  );
}

/**
 * Et eget ventende notat for tenåring/barn (#211): teksten og Trekk tilbake.
 * Trekk tilbake er `POST /:id/withdraw` (#218), som bare sletter mens notatet
 * venter. Mutasjonen og feilen eies av ruten: ved 409 hentes lista på nytt og
 * raden forsvinner, så en feil i raden ville forsvunnet med den.
 */
function OwnPendingRow({
  note,
  onWithdraw,
  busy,
}: {
  note: MemoryDTO;
  onWithdraw: (id: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li data-testid="memory-own-pending" className="flex flex-col gap-1 border-t border-card-line py-2.5 first:border-t-0">
      <p className="m-0 text-sm font-bold text-ink">{note.text}</p>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          variant="secondary"
          size={44}
          data-testid="memory-withdraw"
          aria-label={t('assistant.memory.own_pending.withdraw_label', { text: note.text })}
          disabled={busy}
          onClick={() => onWithdraw(note.id)}
        >
          {t('assistant.memory.own_pending.withdraw')}
        </Button>
      </div>
    </li>
  );
}

/**
 * «Arkivert» (#227): notatene om arkiverte medlemmer, sammenfoldet som
 * standard. Serveren gir dem bare til admin, og de er ute av prompten; her
 * kan de bare slettes — de kommer tilbake av seg selv hvis medlemmet
 * gjenopprettes. Knappen er seksjonens overskrift, med `aria-expanded`
 * som «Avkrysset» i handlelista.
 */
function ArchivedSection({ groups }: { groups: readonly ArchivedMemberMemories[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const headingId = useId();
  const count = groups.reduce((n, g) => n + g.memories.length, 0);
  return (
    <section
      aria-labelledby={headingId}
      data-testid="memory-archived-section"
      className={`${ADMIN_CARD} flex flex-col gap-1`}
    >
      <h2 className="m-0">
        <button
          type="button"
          data-testid="memory-archived-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className={`${ADMIN_LABEL} flex min-h-11 w-full items-center justify-between gap-2 text-left`}
        >
          <span id={headingId} className="tnum">
            {t('assistant.memory.archived.toggle', { count })}
          </span>
          <span className={`inline-flex transition-transform ${open ? 'rotate-90' : ''}`}>
            <Icon.ChevR size={18} />
          </span>
        </button>
      </h2>
      {open ? (
        <div id={panelId} className="flex flex-col gap-1">
          <p className="m-0 text-caption text-ink-2">{t('assistant.memory.archived.hint')}</p>
          {groups.map((group) => (
            <div key={group.member_id} data-testid={`memory-archived-${group.member_id}`}>
              <h3 className="m-0 pt-2 text-caption font-bold text-ink-2">{group.name}</h3>
              <ul className="m-0 list-none p-0">
                {group.memories.map((note) => (
                  // Bare Slett: ingen målgruppe- eller tekstendring på et
                  // skjult notat (eiers beslutning i #227).
                  <NoteRow key={note.id} note={note} isAdmin={false} canDelete />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// `data-testid` spres videre i stedet for å sendes som en vanlig prop, så
// e2e-kontrakten (e2e-testid-contract.test.ts) leser verdien på kallstedet.
function Section({
  label,
  hint,
  children,
  ...rest
}: {
  label: string;
  hint?: string;
  children: ReactElement[];
  'data-testid': string;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className={`${ADMIN_CARD} flex flex-col gap-1`} {...rest}>
      <h2 id={headingId} className={ADMIN_LABEL}>
        {label}
      </h2>
      {hint ? <p className="m-0 text-caption text-ink-2">{hint}</p> : null}
      <ul className="m-0 list-none p-0">{children}</ul>
    </section>
  );
}

export function MemorySettingsRoute(): ReactElement {
  const { t } = useTranslation();
  const session = useSession();
  const me = session.data?.member ?? null;
  // Feiler lukket (#24): bare et bekreftet admin-medlem får adminvisningen.
  const isAdmin = me?.role === 'admin';
  const memories = useMemories();
  const withdraw = useWithdrawMemory();
  const { data: members = [] } = useFamilyMembers();

  const wholeFamily = t('assistant.memory.settings.whole_family');
  const former = t('assistant.memory.settings.former_member');
  const nameOf: NameOf = (id) =>
    id === null ? wholeFamily : (members.find((m) => m.id === id)?.name ?? former);

  const header = <ScreenHeader title={t('assistant.memory.settings.title')} back={{ to: '/settings' }} />;

  if (session.isPending || memories.isPending) {
    return (
      <main className={CONSOLE_MAIN}>
        {header}
        <LoadingState lines={3} />
      </main>
    );
  }

  if (!memories.data) {
    return (
      <main className={CONSOLE_MAIN}>
        {header}
        <ErrorState
          variant="inline"
          heading={t('assistant.memory.settings.load_error')}
          onRetry={() => void memories.refetch()}
        />
      </main>
    );
  }

  // `= []`: et API fra før #211 sender ikke feltet (web og api rulles ut
  // samtidig, men en ny bunt kan et øyeblikk møte det gamle API-et).
  // `archived = []` av samme grunn, for et API fra før #227.
  const { enabled, memories: notes, pending, own_pending: ownPending = [], archived = [] } = memories.data;
  const groups = groupNotes(notes, members, wholeFamily, former);

  return (
    <main data-testid="memory-settings-route" className={CONSOLE_MAIN}>
      {header}
      <p className="m-0 text-sm text-ink-2">{t('assistant.memory.settings.intro')}</p>
      {!enabled ? (
        <p data-testid="memory-disabled" className="m-0 text-sm font-bold text-ink">
          {t('assistant.memory.settings.disabled')}
        </p>
      ) : null}
      {!isAdmin ? (
        <p data-testid="memory-parents-can-see" className="m-0 text-sm font-bold text-ink">
          {t('assistant.memory.settings.parents_can_see')}
        </p>
      ) : null}

      {isAdmin && pending.length > 0 ? (
        <Section label={t('assistant.memory.settings.pending_heading')} data-testid="memory-pending-section">
          {pending.map((note) => (
            <PendingRow key={note.id} note={note} nameOf={nameOf} />
          ))}
        </Section>
      ) : null}

      {/* Utenfor seksjonen (#218): etter en 409 er notatet ikke lenger
          ventende, og seksjonen kan forsvinne når lista hentes på nytt. */}
      {withdraw.error ? (
        <p role="alert" data-testid="memory-withdraw-error" className="m-0 text-sm font-bold text-ink">
          {withdrawErrorText(t, withdraw.error)}
        </p>
      ) : null}

      {/* Egne ventende (#211). Serveren gir dem bare til tenåring/barn, og
          bare deres egne; `!isAdmin` er bare ryddighet. */}
      {!isAdmin && ownPending.length > 0 ? (
        <Section
          label={t('assistant.memory.settings.pending_heading')}
          hint={t('assistant.memory.own_pending.hint')}
          data-testid="memory-own-pending-section"
        >
          {ownPending.map((note) => (
            <OwnPendingRow
              key={note.id}
              note={note}
              busy={withdraw.isPending}
              onWithdraw={(id) => withdraw.mutate(id)}
            />
          ))}
        </Section>
      ) : null}

      {notes.length === 0 && pending.length === 0 && ownPending.length === 0 && archived.length === 0 ? (
        <EmptyState
          heading={t('assistant.memory.settings.empty_heading')}
          body={t('assistant.memory.settings.empty_body')}
        />
      ) : null}

      {groups.map((group) => (
        <Section key={group.key} label={group.label} data-testid={`memory-group-${group.key}`}>
          {group.notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              isAdmin={isAdmin}
              canDelete={isAdmin || (me !== null && note.created_by === me.id)}
            />
          ))}
        </Section>
      ))}

      {/* Serveren gir `archived` bare til admin; `isAdmin` er bare ryddighet. */}
      {isAdmin && archived.length > 0 ? <ArchivedSection groups={archived} /> : null}
    </main>
  );
}
